import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { 
  FaUsers, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaSchool, 
  FaCity, 
  FaGraduationCap, 
  FaChartBar,
  FaSync,
  FaExclamationTriangle
} from 'react-icons/fa';

const StatisticsDashboard = () => {
  const { adminToken } = useAdminAuth();

  const fetchStatistics = async () => {
    const response = await fetch('https://question-manager-backend.onrender.com/api/dashboard/admin/statistics', {
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('فشل في جلب البيانات');
    }
    
    return response.json();
  };

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['adminStatistics'],
    queryFn: fetchStatistics,
    refetchInterval: 300000,
  });

  const SkeletonCard = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 animate-pulse">
      <div className="flex items-center mb-3">
        <div className="bg-gray-200 p-2 rounded-xl ml-3 w-10 h-10"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded-lg w-16 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-20"></div>
    </div>
  );

  const SkeletonList = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 animate-pulse">
      <div className="h-5 bg-gray-200 rounded-lg w-32 mb-4"></div>
      {[1, 2, 3].map((item) => (
        <div key={item} className="flex justify-between items-center py-3">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-6 bg-gray-200 rounded-full w-8"></div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3">
        <div className="max-w-7xl mx-auto">
          {/* <div className="mb-6">
            <div className="h-7 bg-gray-200 rounded-lg w-48 mb-2 mx-auto animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-40 mx-auto animate-pulse"></div>
          </div> */}

          <div className="mb-6">
            <div className="flex overflow-x-auto pb-4 space-x-3 hide-scrollbar">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="flex-none w-40">
                  <SkeletonCard />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {[1, 2, 3, 4].map((item) => (
              <SkeletonList key={item} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center max-w-md w-full">
          <div className="text-red-400 text-4xl mb-4">
            <FaExclamationTriangle />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-3">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4 text-sm leading-relaxed">{error.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-md hover:shadow-lg w-full flex items-center justify-center gap-2"
          >
            <FaSync />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  const { statistics, lastUpdated } = data;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCards = () => (
    <div className="flex overflow-x-auto pb-4 space-x-3 hide-scrollbar mb-6">
      <div className="flex-none w-40 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-500 to-blue-600"></div>
        <div className="flex items-center mb-3">
          <div className="bg-blue-50 p-2 rounded-xl ml-2 text-blue-600">
            <FaUsers size={16} />
          </div>
          <h3 className="text-xs font-semibold text-gray-600">المستخدمين</h3>
        </div>
        <p className="text-2xl font-bold text-gray-800 mb-1">{statistics.users.totalUsers}</p>
        <p className="text-xs text-gray-500">مستخدم مسجل</p>
      </div>

      <div className="flex-none w-40 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-green-500 to-green-600"></div>
        <div className="flex items-center mb-3">
          <div className="bg-green-50 p-2 rounded-xl ml-2 text-green-600">
            <FaFileAlt size={16} />
          </div>
          <h3 className="text-xs font-semibold text-gray-600">الاختبارات</h3>
        </div>
        <p className="text-2xl font-bold text-gray-800 mb-1">{statistics.tests.total}</p>
        <div className="flex gap-1">
          <span className="text-xs text-blue-600 bg-blue-50 px-1 rounded">ع: {statistics.tests.public}</span>
          <span className="text-xs text-purple-600 bg-purple-50 px-1 rounded">خ: {statistics.tests.private}</span>
        </div>
      </div>

\      <div className="flex-none w-40 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-purple-500 to-purple-600"></div>
        <div className="flex items-center mb-3">
          <div className="bg-purple-50 p-2 rounded-xl ml-2 text-purple-600">
            <FaQuestionCircle size={16} />
          </div>
          <h3 className="text-xs font-semibold text-gray-600">الأسئلة</h3>
        </div>
        <p className="text-2xl font-bold text-gray-800 mb-1">{statistics.questions.total}</p>
        <p className="text-xs text-gray-500">سؤال في النظام</p>
      </div>

      <div className="flex-none w-40 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-orange-500 to-orange-600"></div>
        <div className="flex items-center mb-3">
          <div className="bg-orange-50 p-2 rounded-xl ml-2 text-orange-600">
            <FaSchool size={16} />
          </div>
          <h3 className="text-xs font-semibold text-gray-600">الفصول</h3>
        </div>
        <p className="text-2xl font-bold text-gray-800 mb-1">{statistics.classes.total}</p>
        <p className="text-xs text-gray-500">فصل دراسي</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3">
      <div className="max-w-7xl mx-auto">
        {/* <div className="mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
            <h1 className="text-xl font-bold text-gray-800 mb-2 text-center bg-gradient-to-l from-blue-600 to-purple-600 bg-clip-text text-transparent">
              لوحة التحكم - الإحصائيات
            </h1>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <FaSync size={12} />
              <span>آخر تحديث: {formatDate(lastUpdated)}</span>
            </div>
          </div>
        </div> */}

        <StatCards />

        <div className="grid grid-cols-1 gap-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <div className="bg-blue-50 p-2 rounded-lg ml-2 text-blue-600">
                <FaCity size={16} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">المستخدمين حسب المدينة</h3>
            </div>
            <div className="space-y-2">
              {statistics.users.byCity.map((city, index) => (
                <div key={index} className="flex justify-between items-center py-2 px-3 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full ml-2"></span>
                    <span className="text-sm text-gray-700 font-medium">{city.city}</span>
                  </div>
                  <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-bold min-w-8 text-center">
                    {city.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <div className="bg-green-50 p-2 rounded-lg ml-2 text-green-600">
                <FaGraduationCap size={16} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">المستخدمين حسب المدرسة</h3>
            </div>
            <div className="space-y-2">
              {statistics.users.bySchool.map((school, index) => (
                <div key={index} className="flex justify-between items-center py-2 px-3 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full ml-2"></span>
                    <span className="text-sm text-gray-700 font-medium">{school.school}</span>
                  </div>
                  <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-xs font-bold min-w-8 text-center">
                    {school.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <div className="bg-purple-50 p-2 rounded-lg ml-2 text-purple-600">
                <FaSchool size={16} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">المستخدمين حسب الفصل</h3>
            </div>
            <div className="space-y-2">
              {statistics.users.byClass.map((classItem, index) => (
                <div key={index} className="flex justify-between items-center py-2 px-3 rounded-xl hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full ml-2"></span>
                    <span className="text-sm text-gray-700 font-medium">{classItem.class}</span>
                  </div>
                  <span className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-xs font-bold min-w-8 text-center">
                    {classItem.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <div className="bg-orange-50 p-2 rounded-lg ml-2 text-orange-600">
                <FaChartBar size={16} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">مستويات الاختبارات</h3>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {statistics.tests.levels.map((level, index) => (
                <div key={index} className="border border-gray-200 rounded-xl p-3 hover:border-blue-300 transition-all duration-300">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-xs leading-relaxed">
                        {level.testTitle} - المستوى {level.levelNumber}
                      </h4>
                      <p className="text-gray-500 text-xs mt-1">الفصل: {level.className}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0 ${
                      level.isPublic ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {level.isPublic ? 'عام' : 'خاص'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center">
                      <span className="text-red-400 ml-1">❤️</span>
                      <span className="text-gray-600">القلوب: {level.heartsPerAttempt}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-blue-400 ml-1">💡</span>
                      <span className="text-gray-600">التلميحات: {level.hintsPerAttempt}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <div className="bg-indigo-50 p-2 rounded-lg ml-2 text-indigo-600">
                <FaSchool size={16} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">تفاصيل الفصول</h3>
            </div>
            <div className="space-y-3">
              {statistics.classes.details.map((classDetail, index) => (
                <div key={index} className="border border-gray-200 rounded-xl p-3 hover:shadow-md transition-all duration-300">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-gray-800 text-sm">{classDetail.name}</h4>
                    <span className="bg-indigo-100 text-indigo-600 px-2 py-1 rounded-full text-xs font-bold">
                      {classDetail.studentCount} طالب
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2 line-clamp-2">
                    {classDetail.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default StatisticsDashboard;